import {
	GameCategory,
	PerformanceCategory,
	SoccerPerformanceCategory,
	TennisPerformanceCategory,
	FootballPerformanceCategory,
} from '../enums';
import { getPerformanceCategoryLabel } from '../enums/performance-category.enum';
import {
	TENNIS_GAME_CATEGORIES,
	FOOTBALL_GAME_CATEGORIES,
	SOCCER_GAME_CATEGORIES,
} from '../enums/optic-odds.enum';
import {
	TENNIS_PERFORMANCE_CATEGORY_BY_MARKET,
	TENNIS_PERFORMANCE_CATEGORY_BY_SEEDED_CATEGORY,
} from './market/tennis-performance.config';
import {
	SOCCER_PERFORMANCE_CATEGORY_BY_MARKET,
	SOCCER_PERFORMANCE_CATEGORY_BY_SEEDED_CATEGORY,
} from './market/soccer-performance.config';
import {
	FOOTBALL_PERFORMANCE_CATEGORY_BY_MARKET,
	FOOTBALL_PERFORMANCE_CATEGORY_BY_SEEDED_CATEGORY,
} from './market/football-performance.config';

function resolveCategoryFromMaps<T extends string>(
	byMarket: Record<string, T>,
	bySeededCategory: Record<string, T>,
	extApiMarket?: string | null,
	categoryName?: string | null
): T | null {
	const byMarketMatch = extApiMarket ? byMarket[extApiMarket.trim()] : undefined;
	if (byMarketMatch) {
		return byMarketMatch;
	}

	const bySeededCategoryMatch = categoryName ? bySeededCategory[categoryName.trim()] : undefined;
	return bySeededCategoryMatch ?? null;
}

interface SportPerformanceCategoryModule<T extends PerformanceCategory = PerformanceCategory> {
	categories: T[];
	byMarket: Record<string, T>;
	resolve: (extApiMarket?: string | null, categoryName?: string | null) => T | null;
}

const soccerModule: SportPerformanceCategoryModule<SoccerPerformanceCategory> = {
	categories: Object.values(SoccerPerformanceCategory),
	byMarket: SOCCER_PERFORMANCE_CATEGORY_BY_MARKET,
	resolve: (extApiMarket, categoryName) =>
		resolveCategoryFromMaps(
			SOCCER_PERFORMANCE_CATEGORY_BY_MARKET,
			SOCCER_PERFORMANCE_CATEGORY_BY_SEEDED_CATEGORY,
			extApiMarket,
			categoryName
		),
};

const tennisModule: SportPerformanceCategoryModule<TennisPerformanceCategory> = {
	categories: Object.values(TennisPerformanceCategory),
	byMarket: TENNIS_PERFORMANCE_CATEGORY_BY_MARKET,
	resolve: (extApiMarket, categoryName) =>
		resolveCategoryFromMaps(
			TENNIS_PERFORMANCE_CATEGORY_BY_MARKET,
			TENNIS_PERFORMANCE_CATEGORY_BY_SEEDED_CATEGORY,
			extApiMarket,
			categoryName
		),
};

const footballModule: SportPerformanceCategoryModule<FootballPerformanceCategory> = {
	categories: Object.values(FootballPerformanceCategory),
	byMarket: FOOTBALL_PERFORMANCE_CATEGORY_BY_MARKET,
	resolve: (extApiMarket, categoryName) =>
		resolveCategoryFromMaps(
			FOOTBALL_PERFORMANCE_CATEGORY_BY_MARKET,
			FOOTBALL_PERFORMANCE_CATEGORY_BY_SEEDED_CATEGORY,
			extApiMarket,
			categoryName
		),
};

export const resolveSoccerPerformanceCategory = soccerModule.resolve;
export const resolveTennisPerformanceCategory = tennisModule.resolve;
export const resolveFootballPerformanceCategory = footballModule.resolve;

const DEFAULT_SPORT_PERFORMANCE_MODULE = soccerModule;

const SPORT_PERFORMANCE_MODULES: Partial<Record<GameCategory, SportPerformanceCategoryModule>> =
	Object.fromEntries(
		Object.values(GameCategory).map((category) => {
			if (TENNIS_GAME_CATEGORIES.has(category)) return [category, tennisModule];
			if (FOOTBALL_GAME_CATEGORIES.has(category)) return [category, footballModule];
			if (SOCCER_GAME_CATEGORIES.has(category)) return [category, soccerModule];
			return [category, soccerModule];
		})
	);

function getSportPerformanceModule(sportId?: string | null): SportPerformanceCategoryModule {
	return SPORT_PERFORMANCE_MODULES[sportId as GameCategory] ?? DEFAULT_SPORT_PERFORMANCE_MODULE;
}

export function getPerformanceCategoriesForSport(sportId?: string | null): PerformanceCategory[] {
	return getSportPerformanceModule(sportId).categories;
}

export function resolvePerformanceCategoryForSport(
	sportId: string | null | undefined,
	extApiMarket?: string | null,
	categoryName?: string | null
): PerformanceCategory | null {
	return getSportPerformanceModule(sportId).resolve(extApiMarket, categoryName);
}

export interface PerformanceCategoryGroup {
	category: PerformanceCategory;
	label: string;
	subcategories: string[];
}

function buildPerformanceCategoryGroups(
	module: SportPerformanceCategoryModule
): PerformanceCategoryGroup[] {
	const subcategoriesByCategory = new Map<PerformanceCategory, string[]>();
	for (const category of module.categories) {
		subcategoriesByCategory.set(category, []);
	}
	for (const [market, category] of Object.entries(module.byMarket)) {
		subcategoriesByCategory.get(category)?.push(market);
	}
	return module.categories.map((category) => ({
		category,
		label: getPerformanceCategoryLabel(category) ?? category,
		subcategories: subcategoriesByCategory.get(category) ?? [],
	}));
}

export function getAllPerformanceCategoryGroups(): PerformanceCategoryGroup[] {
	const modules = new Set<SportPerformanceCategoryModule>([
		...Object.values(SPORT_PERFORMANCE_MODULES),
		DEFAULT_SPORT_PERFORMANCE_MODULE,
	]);
	return Array.from(modules).flatMap(buildPerformanceCategoryGroups);
}
